import { useState, useRef, useCallback, useEffect, type KeyboardEvent } from 'react'
import { useAuthStore } from '../../stores/useAuthStore.js'
import { AttachmentButton } from './AttachmentButton.js'
import { AudioRecorder } from './AudioRecorder.js'
import { EmojiPicker } from './EmojiPicker.js'
import { AttachmentsPreview } from './AttachmentsPreview.js'

interface Attachment {
  url: string
  type: string
  name: string
}

interface PendingFile {
  file: File
  url: string
  type: string
  name: string
}

interface ReplyBoxProps {
  onSend: (content: string, attachments?: Attachment[]) => void
  disabled?: boolean
  /** Files dropped from outside (e.g. drag-drop on message area) */
  droppedFiles?: File[]
  onDroppedFilesConsumed?: () => void
}

export function ReplyBox({ onSend, disabled, droppedFiles, onDroppedFilesConsumed }: ReplyBoxProps) {
  const [text, setText] = useState('')
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [isRecordingAudio, setIsRecordingAudio] = useState(false)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const tokens = useAuthStore((s) => s.tokens)

  // Accept files dropped from parent
  useEffect(() => {
    if (droppedFiles && droppedFiles.length > 0) {
      addFiles(droppedFiles)
      onDroppedFilesConsumed?.()
    }
  }, [droppedFiles]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleInput() {
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`
    }
  }

  async function uploadFile(file: File): Promise<Attachment | null> {
    if (!tokens) return null
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await fetch('/api/uploads', {
        method: 'POST',
        headers: {
          'access-token': tokens.accessToken,
          client: tokens.client,
          uid: tokens.uid,
        },
        body: formData,
      })
      if (!response.ok) return null
      const { data } = await response.json()
      return data
    } catch {
      return null
    }
  }

  const handleSend = useCallback(async () => {
    const trimmed = text.trim()
    const hasAudio = audioFile !== null
    const hasFiles = pendingFiles.length > 0

    if (!trimmed && !hasFiles && !hasAudio) return

    setIsUploading(true)
    try {
      const uploadedAttachments: Attachment[] = []

      if (hasAudio) {
        const uploaded = await uploadFile(audioFile)
        if (uploaded) uploadedAttachments.push(uploaded)
      }

      for (const pf of pendingFiles) {
        const uploaded = await uploadFile(pf.file)
        if (uploaded) uploadedAttachments.push(uploaded)
      }

      if (uploadedAttachments.length === 0 && (hasAudio || hasFiles)) {
        setUploadError('Falha ao enviar arquivo(s). Tipo não permitido ou erro no servidor.')
        setTimeout(() => setUploadError(null), 5000)
        return
      }

      onSend(trimmed, uploadedAttachments.length > 0 ? uploadedAttachments : undefined)
      setText('')
      setPendingFiles([])
      setAudioFile(null)
      setIsRecordingAudio(false)
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    } finally {
      setIsUploading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, pendingFiles, audioFile, onSend, tokens])

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const addFiles = useCallback((files: File[]) => {
    const MAX_SIZE = 20 * 1024 * 1024 // 20MB
    const rejected: string[] = []
    const accepted: File[] = []

    for (const file of files) {
      if (file.size > MAX_SIZE) {
        rejected.push(`${file.name} (excede 20MB)`)
        continue
      }
      accepted.push(file)
    }

    if (rejected.length > 0) {
      setUploadError(`Arquivo(s) rejeitado(s): ${rejected.join(', ')}`)
      setTimeout(() => setUploadError(null), 5000)
    }

    if (accepted.length > 0) {
      const newPending = accepted.map((file) => ({
        file,
        url: URL.createObjectURL(file),
        type: file.type,
        name: file.name,
      }))
      setPendingFiles((prev) => [...prev, ...newPending])
    }
  }, [])

  const removeFile = useCallback((index: number) => {
    setPendingFiles((prev) => {
      const removed = prev[index]
      if (removed) URL.revokeObjectURL(removed.url)
      return prev.filter((_, i) => i !== index)
    })
  }, [])

  useEffect(() => {
    return () => {
      pendingFiles.forEach((pf) => URL.revokeObjectURL(pf.url))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const files = Array.from(e.clipboardData.files).filter((f) => f.size > 0)
      if (files.length > 0) addFiles(files)
    },
    [addFiles],
  )

  const handleAudioReady = useCallback((file: File) => {
    setAudioFile(file)
  }, [])

  const handleAudioCancel = useCallback(() => {
    setIsRecordingAudio(false)
    setAudioFile(null)
  }, [])

  const handleEmojiSelect = useCallback((emoji: string) => {
    setText((prev) => prev + emoji)
    textareaRef.current?.focus()
  }, [])

  const toggleEmojiPicker = useCallback(() => {
    setShowEmojiPicker((prev) => !prev)
  }, [])

  const closeEmojiPicker = useCallback(() => {
    setShowEmojiPicker(false)
  }, [])

  const canSend = text.trim().length > 0 || pendingFiles.length > 0 || audioFile !== null
  const isDisabled = disabled || isUploading

  return (
    <div className="relative mx-2 mb-2 rounded-xl border border-(--color-border) bg-(--color-bg-primary) transition-all">
      {/* Emoji picker — positioned above the entire reply box */}
      {showEmojiPicker && (
        <div className="absolute bottom-full left-2 z-50 mb-1">
          <EmojiPicker onSelect={handleEmojiSelect} onClose={closeEmojiPicker} />
        </div>
      )}

      {/* Audio recorder replaces editor when active */}
      {isRecordingAudio ? (
        <AudioRecorder onReady={handleAudioReady} onCancel={handleAudioCancel} />
      ) : (
        <>
          {/* Editor area */}
          <div className="relative px-4 pt-3 pb-1">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              onInput={handleInput}
              onPaste={handlePaste}
              placeholder="Digite uma mensagem..."
              disabled={isDisabled}
              rows={1}
              className="w-full resize-none bg-transparent text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted) focus:outline-none disabled:opacity-50"
              style={{ minHeight: '24px', maxHeight: '120px' }}
            />
          </div>

          {/* Attachments preview */}
          {pendingFiles.length > 0 && (
            <AttachmentsPreview attachments={pendingFiles} onRemove={removeFile} />
          )}
        </>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="mx-3 mb-1 rounded-lg bg-(--color-danger)/10 px-3 py-1.5 text-xs text-(--color-danger)">
          {uploadError}
        </div>
      )}

      {/* Bottom toolbar */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-1">
          {/* Emoji */}
          <button
            onClick={toggleEmojiPicker}
            disabled={isDisabled || isRecordingAudio}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-(--color-text-muted) hover:bg-(--color-bg-tertiary) hover:text-(--color-text-primary) transition-colors disabled:opacity-50"
            title="Emoji"
          >
            <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
            </svg>
          </button>

          {/* Attachment */}
          <AttachmentButton
            onFilesSelected={addFiles}
            disabled={isDisabled || isRecordingAudio}
          />

          {/* Audio recorder toggle */}
          <button
            onClick={() => {
              if (isRecordingAudio) {
                handleAudioCancel()
              } else {
                setIsRecordingAudio(true)
              }
            }}
            disabled={isDisabled}
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors disabled:opacity-50 ${
              isRecordingAudio
                ? 'text-(--color-danger) hover:bg-(--color-danger)/10'
                : 'text-(--color-text-muted) hover:bg-(--color-bg-tertiary) hover:text-(--color-text-primary)'
            }`}
            title={isRecordingAudio ? 'Cancelar gravação' : 'Gravar áudio'}
          >
            <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              {isRecordingAudio ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3zM3.75 3.75l16.5 16.5" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
              )}
            </svg>
          </button>
        </div>

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={isDisabled || !canSend}
          className="flex h-8 items-center gap-1.5 rounded-lg bg-(--color-primary) px-3 text-white text-sm font-medium transition-colors hover:bg-(--color-primary-dark) disabled:opacity-50"
        >
          {isUploading ? (
            <>
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Enviando
            </>
          ) : (
            <>
              Enviar
              <kbd className="hidden sm:inline-block text-xs opacity-60">↵</kbd>
            </>
          )}
        </button>
      </div>
    </div>
  )
}
